import { makeAutoObservable, runInAction } from 'mobx';
import { customFetch } from '@/shared/api';

const NEXT_PUBLIC_API_URL_CLIENT_SIDE =
  process.env.NEXT_PUBLIC_API_URL_CLIENT_SIDE;

class AvatarStore {
  // Observable state
  avatarUrl: string | null = null;
  loading: boolean = false;
  error: string | null = null;
  optimisticUrl: string | null = null; // For instant updates
  currentAvatarPath: string | null = null; // Track current path

  constructor() {
    makeAutoObservable(this);
  }

  // Computed - final URL for display
  get finalAvatarUrl() {
    return this.optimisticUrl || this.avatarUrl;
  }

  // Actions
  setAvatarUrl(url: string | null) {
    this.avatarUrl = url;
  }

  setLoading(loading: boolean) {
    this.loading = loading;
  }

  setError(error: string | null) {
    this.error = error;
  }

  setOptimisticUrl(url: string | null) {
    this.optimisticUrl = url;
    console.log('🎯 Set optimistic URL:', url);
  }

  clearOptimistic() {
    if (this.optimisticUrl) {
      console.log('🧹 Clearing optimistic URL:', this.optimisticUrl);
      this.optimisticUrl = null;
    }
  }

  reset() {
    console.log('🔄 Resetting avatar store');
    this.avatarUrl = null;
    this.loading = false;
    this.error = null;
    this.optimisticUrl = null;
    this.currentAvatarPath = null;
  }

  async loadAvatar(avatarPath: string, retries = 3): Promise<void> {
    this.loading = true;
    try {
      const response = await customFetch(
        `${NEXT_PUBLIC_API_URL_CLIENT_SIDE}/avatar`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ path: avatarPath }),
        }
      );

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.startsWith('image/')) {
        if (retries > 0) {
          // Wait and try again
          await new Promise((res) => setTimeout(res, 700));
          return this.loadAvatar(avatarPath, retries - 1);
        }
        const text = await response.text();
        console.error(
          '❌ Avatar API returned not an image:',
          contentType,
          text
        );
        this.avatarUrl = null;
        this.loading = false;
        return;
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      this.avatarUrl = url;
      this.loading = false;
    } catch (e) {
      console.error('❌ Avatar fetch error:', e);
      this.avatarUrl = null;
      this.loading = false;
    }
  }

  // Forced reload
  async forceReload(avatarPath: string) {
    console.log('🔄 Force reloading avatar for:', avatarPath);

    // Clear old blob-URL
    if (this.avatarUrl && this.avatarUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.avatarUrl);
    }

    runInAction(() => {
      this.avatarUrl = null;
      this.currentAvatarPath = null;
      this.optimisticUrl = null;
    });

    // Reload
    await this.loadAvatar(avatarPath);
  }

  // Update avatar path (called when session changes)
  updateAvatarPath(newPath: string | null) {
    if (this.currentAvatarPath === newPath) {
      // If avatarUrl is missing but path is the same — load!
      if (!this.avatarUrl && newPath) {
        this.loadAvatar(newPath);
      }
      // else skip
      return;
    }
    // If path changed — reset and load new
    this.currentAvatarPath = newPath;
    this.avatarUrl = null;
    if (newPath) {
      this.loadAvatar(newPath);
    }
  }
}

// Create singleton instance
export const avatarStore = new AvatarStore();
